import { Role, PatientStatusCode, GlucoseFilesThreshold } from '../config/DiactiveTypes';
import { calculatePostEvalDate, getPatientFullName, getPatientHspId } from './DiactiveUtils';

import PreEvalList from '../data/questionnaires/PreEvalList.json';
import PostEvalList from '../data/questionnaires/PostEvalList.json';

const PRE_QUESTIONNAIRES_DENOMINATOR = Math.max(0, PreEvalList.length - 1);
const POST_QUESTIONNAIRES_DENOMINATOR_INTR = Math.max(0, PostEvalList.length - 1);
const POST_ONLY_INTR_COUNT = PostEvalList.filter((q) => q.onlyIntr).length;
const POST_QUESTIONNAIRES_DENOMINATOR_CTRL = Math.max(0, POST_QUESTIONNAIRES_DENOMINATOR_INTR - POST_ONLY_INTR_COUNT);

export const matchesSearch = (patient, { role, searchQuery }) => {
  if (role !== Role.Doctor) return true;
  const patientName = getPatientFullName(patient);
  return patientName.toLowerCase().includes(searchQuery.toLowerCase());
};

export const matchesHospital = (patient, hospitalId) => {
  return hospitalId === '' || patient.hospital === hospitalId;
};

export const matchesGroup = (patient, groupRole) => {
  return groupRole === '' || patient.role === groupRole;
};

export const matchesStatus = (patient, statusCode) => {
  return statusCode === '' || patient.status?.code === statusCode;
};

export const filterPatients = (patients, { role, searchQuery, selectedHospital, selectedGroup, selectedStatus }) => {
  return patients.filter((patient) => (
    matchesSearch(patient, { role, searchQuery }) &&
    matchesHospital(patient, selectedHospital) &&
    matchesGroup(patient, selectedGroup) &&
    matchesStatus(patient, selectedStatus)
  ));
};

export const sortPatients = (patients, sortConfig, { selectedStatus } = {}) => {
  const collator = new Intl.Collator('es', { sensitivity: 'base', numeric: true });

  // For phase-dependent counters, pick the phase from the currently selected status.
  const pendingPhase =
    selectedStatus === PatientStatusCode.PRE_PENDING ? 'pre' :
    selectedStatus === PatientStatusCode.POST_PENDING ? 'post' : null;

  const sorted = [...patients].sort((a, b) => {
    let aValue;
    let bValue;

    switch (sortConfig.key) {
      case 'createdAt':
        aValue = a.createdAt;
        bValue = b.createdAt;
        break;
      case 'name':
        aValue = getPatientFullName(a);
        bValue = getPatientFullName(b);
        break;
      case 'nickname':
        aValue = a.nickname ?? '';
        bValue = b.nickname ?? '';
        break;
      case 'id':
        aValue = getPatientHspId(a);
        bValue = getPatientHspId(b);
        break;
      case 'daysRemaining':
        aValue = a.daysRemaining;
        bValue = b.daysRemaining;
        break;
      case 'postEvalDate':
        aValue = calculatePostEvalDate(a.countdownStartedAt)?.getTime() ?? 0;
        bValue = calculatePostEvalDate(b.countdownStartedAt)?.getTime() ?? 0;
        break;
      case 'pendingQuestionnaires':
        aValue = pendingPhase ? (a.questionnairesCount?.[pendingPhase] ?? 0) : 0;
        bValue = pendingPhase ? (b.questionnairesCount?.[pendingPhase] ?? 0) : 0;
        break;
      case 'pendingGlucoseFiles':
        aValue = pendingPhase ? (a.glucoseFileCount?.[pendingPhase] ?? 0) : 0;
        bValue = pendingPhase ? (b.glucoseFileCount?.[pendingPhase] ?? 0) : 0;
        break;
      case 'workoutsThisWeek':
        aValue = a.workoutsThisWeek ?? 0;
        bValue = b.workoutsThisWeek ?? 0;
        break;
      default:
        aValue = a[sortConfig.key] || '';
        bValue = b[sortConfig.key] || '';
    }

    // Numeric compare for numeric fields; locale+accent-insensitive compare for strings.
    const isNumeric = typeof aValue === 'number' && typeof bValue === 'number';
    const comp = isNumeric ? (aValue - bValue) : collator.compare(String(aValue ?? ''), String(bValue ?? ''));

    return sortConfig.direction === 'asc' ? comp : -comp;
  });

  return sorted;
};

export const formatPendingGlucoseFiles = ({ statusCode, glucoseFileCount }) => {
  if (statusCode !== PatientStatusCode.PRE_PENDING && statusCode !== PatientStatusCode.POST_PENDING) return '—/—';

  const pendingPhase = statusCode === PatientStatusCode.PRE_PENDING ? 'pre' : 'post';
  const glucoseCount = glucoseFileCount?.[pendingPhase];

  if (glucoseCount === undefined || glucoseCount === null) return '—/—';

  const denominator = Math.max(GlucoseFilesThreshold, glucoseCount);
  return `${glucoseCount}/${denominator}`;
};

export const formatPendingQuestionnairesFiles = ({ statusCode, questionnairesCount, patientRole }) => {
  if (statusCode !== PatientStatusCode.PRE_PENDING && statusCode !== PatientStatusCode.POST_PENDING) return '—/—';

  const pendingPhase = statusCode === PatientStatusCode.PRE_PENDING ? 'pre' : 'post';
  const numerator = questionnairesCount?.[pendingPhase];

  if (numerator === undefined || numerator === null) return '—/—';

  let fixedDenominator;
  if (pendingPhase === 'pre') {
    fixedDenominator = PRE_QUESTIONNAIRES_DENOMINATOR;
  } else {
    fixedDenominator =
      patientRole === Role.Intr25 ? POST_QUESTIONNAIRES_DENOMINATOR_INTR : POST_QUESTIONNAIRES_DENOMINATOR_CTRL;
  }

  const denominator = Math.max(fixedDenominator, numerator);
  return `${numerator}/${denominator}`;
};

